import type { CSSProperties } from "react";
import { Link } from "@tanstack/react-router";
import { ArrowUpRightIcon } from "lucide-react";

import { Badge } from "~/components/ui/badge";
import { cn } from "~/lib/utils";

import { CatalogSectionHeading } from "./CatalogSectionHeading";
import { RecentlyDeletedSection } from "./RecentlyDeletedSection";
import {
  DASHBOARD_CATALOG_SECTIONS,
  getLiveDashboardRoute,
  type DashboardCatalogItem,
} from "./catalog";

function CatalogCard(props: { item: DashboardCatalogItem }) {
  const { item } = props;
  const isLive = item.availability === "live";
  const liveRoute = getLiveDashboardRoute(item.slug);
  const linkClassName = cn(
    "group flex h-full min-h-0 flex-col overflow-hidden rounded-xl border border-border/70 bg-card text-left no-underline shadow-xs/5",
    "transition-[transform,box-shadow,border-color] duration-200",
    "hover:-translate-y-0.5 hover:border-[color-mix(in_srgb,var(--dash-accent)_42%,var(--border))] hover:shadow-md/10",
    "focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background focus-visible:outline-none",
  );
  const cardStyle = {
    "--dash-accent": item.color,
  } as CSSProperties;
  const body = (
    <>
      <div
        className="relative h-[4.25rem] shrink-0 overflow-hidden"
        style={{
          background: `linear-gradient(145deg, ${item.color} 0%, color-mix(in srgb, ${item.color} 42%, var(--background)) 100%)`,
        }}
      >
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 opacity-30"
          style={{
            backgroundImage:
              "radial-gradient(circle at 86% 18%, #fff 0 16%, transparent 42%), linear-gradient(115deg, transparent 35%, color-mix(in srgb, #fff 22%, transparent) 100%)",
          }}
        />
        <div
          aria-hidden
          className="pointer-events-none absolute -right-6 -bottom-10 size-28 rounded-full opacity-25"
          style={{ background: item.face }}
        />
      </div>
      <div className="flex flex-1 flex-col gap-2.5 px-4 pt-4 pb-3.5">
        <div className="flex items-start justify-between gap-2">
          <h3 className="min-w-0 text-[0.95rem] leading-snug font-semibold tracking-tight text-foreground">
            {item.label}
          </h3>
          {isLive ? (
            <Badge size="sm" variant="success" className="shrink-0">
              Live
            </Badge>
          ) : (
            <Badge size="sm" variant="outline" className="shrink-0">
              Coming soon
            </Badge>
          )}
        </div>
        <p className="line-clamp-3 text-xs leading-relaxed text-muted-foreground">
          {item.description}
        </p>
        <div className="mt-auto flex items-center justify-between pt-1">
          <span className="text-xs font-medium text-muted-foreground transition-colors group-hover:text-foreground">
            {isLive ? "Open dashboard" : "Preview"}
          </span>
          <ArrowUpRightIcon className="size-3.5 text-muted-foreground transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 group-hover:text-foreground" />
        </div>
      </div>
    </>
  );

  if (isLive && liveRoute) {
    return (
      <Link to={liveRoute} className={linkClassName} style={cardStyle}>
        {body}
      </Link>
    );
  }

  return (
    <Link
      to="/dashboards/$dashboardId"
      params={{ dashboardId: item.slug }}
      className={cn(linkClassName, "opacity-90")}
      style={cardStyle}
    >
      {body}
    </Link>
  );
}

/** Sectioned catalog home for `/dashboards`. */
export function DashboardsCatalog() {
  return (
    <div className="w-full min-w-0 px-6 py-8 sm:px-8 sm:py-10">
      <header className="mb-9 space-y-2">
        <h1 className="text-2xl font-semibold tracking-tight text-foreground">Dashboards</h1>
        <p className="max-w-2xl text-sm leading-relaxed text-muted-foreground">
          Open a dashboard the team already uses.
        </p>
      </header>

      <div className="flex flex-col gap-10">
        {DASHBOARD_CATALOG_SECTIONS.map((section) => (
          <section key={section.title} className="space-y-4">
            <CatalogSectionHeading icon={section.icon}>{section.title}</CatalogSectionHeading>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {section.items.map((item) => (
                <CatalogCard key={item.slug} item={item} />
              ))}
            </div>
          </section>
        ))}
        <RecentlyDeletedSection />
      </div>
    </div>
  );
}
